// Standard justification texts for deviating from the FOA § 7-9 30 % climate/environment
// weighting rule. The two lawful exceptions (negligible impact, or requirements in the
// specification give a clearly better effect) each get a starting template the buyer must
// adapt — KOFA practice (2025/322, 2025/356) requires a real, procurement-specific assessment,
// not a copied boilerplate. Keys are translated in locales/{en,no}/translation.json.

// t('Negligible environmental impact')
// t('The environmental impact of this procurement is negligible because [describe why — e.g. small volume, standardized goods with no meaningful environmental differentiation between suppliers]. Climate and environmental considerations therefore carry little weight in distinguishing the tenders.')
// t('Environmental requirements in the specification')
// t('Climate and environmental considerations are addressed as requirements in the specification rather than as an award criterion, because [describe why this gives a clearly better effect — e.g. a binding minimum requirement guarantees the environmental outcome more effectively than a weighted criterion]. The requirements are: [list the relevant environmental requirements].')

export interface EnvironmentJustificationTemplate {
  key: string;
  titleKey: string;
  bodyKey: string;
}

export const ENVIRONMENT_JUSTIFICATION_TEMPLATES: readonly EnvironmentJustificationTemplate[] = [
  {
    key: "negligible-impact",
    titleKey: "Negligible environmental impact",
    bodyKey:
      "The environmental impact of this procurement is negligible because [describe why — e.g. small volume, standardized goods with no meaningful environmental differentiation between suppliers]. Climate and environmental considerations therefore carry little weight in distinguishing the tenders.",
  },
  {
    key: "addressed-in-specification",
    titleKey: "Environmental requirements in the specification",
    bodyKey:
      "Climate and environmental considerations are addressed as requirements in the specification rather than as an award criterion, because [describe why this gives a clearly better effect — e.g. a binding minimum requirement guarantees the environmental outcome more effectively than a weighted criterion]. The requirements are: [list the relevant environmental requirements].",
  },
];

export type EnvironmentJustificationTemplateKey =
  (typeof ENVIRONMENT_JUSTIFICATION_TEMPLATES)[number]["key"];

// The translated body text for a template, used to pre-fill the justification box. `translate` is
// the i18next `t` (untyped to avoid pulling the TFunction generic into a util module).
export const templateBody = (
  key: EnvironmentJustificationTemplateKey,
  translate: (k: string) => string,
): string => {
  const template = ENVIRONMENT_JUSTIFICATION_TEMPLATES.find((tpl) => tpl.key === key);
  return template ? translate(template.bodyKey) : "";
};

// True when the justification still equals the unedited template body — drives the non-blocking
// "you forgot to adapt the standard text" warning. KOFA practice requires a procurement-specific
// assessment, not copied boilerplate. Both sides are trimmed so trailing whitespace doesn't matter.
export const isUnchangedFromTemplate = (text: string, body: string): boolean =>
  text.trim().length > 0 && text.trim() === body.trim();

// True when the justification still equals one of the unedited standard template bodies (in the
// current language). The webapp sends this to the BFF (environmentWeightingJustificationIsStandardText)
// so the publish gate can reject a copied boilerplate without the BFF having to match text — keeping
// the check robust to template/format changes.
export const isStandardText = (text: string, translate: (k: string) => string): boolean =>
  ENVIRONMENT_JUSTIFICATION_TEMPLATES.some((tpl) => isUnchangedFromTemplate(text, translate(tpl.bodyKey)));
